import "./resizeObserverPatch.ts";
import React from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import {
  ExceptionBoundary,
  setupExceptionTracking,
} from "react-3rddigital-exception-tracking";
import { Capacitor } from "@capacitor/core";
import { NativeExceptionHandler } from "capacitor-3rddigital-exception-tracking";
import dayjs from "dayjs";
import updateLocale from "dayjs/plugin/updateLocale";
import moment from "moment";

dayjs.extend(updateLocale);
dayjs.updateLocale("en", {
  weekStart: 1,
});

moment.updateLocale("en", {
  week: {
    dow: 1, // Monday is the first day of the week
  },
});

(function () {
  try {
    var currentPath = window.location.pathname + window.location.search;
    var ignoredPaths = ["/", "/login"];
    var alreadyStored = sessionStorage.getItem("original-deep-link");

    var shouldStore =
      currentPath &&
      !ignoredPaths.includes(window.location.pathname) &&
      !alreadyStored;

    if (shouldStore) {
      sessionStorage.setItem("original-deep-link", currentPath);
    }
  } catch (e) {}
})();

// Preserve specific keys during localStorage.clear()
const originalClear = localStorage.clear;
localStorage.clear = function () {
  const lastActiveAccount = localStorage.getItem("last_active_account");
  const defaultRole = localStorage.getItem("default_role");
  const deviceId = localStorage.getItem("device_id");
  // Device tracking: an owed open / close (or the "already reported" marker) must survive
  // a logout clear — it is attendance data that still has to reach the API, and the tablet
  // may well be offline when the moment it belongs to happened.
  const trackingPendingClose = localStorage.getItem(
    "device_tracking_pending_close_at",
  );
  const trackingPendingStart = localStorage.getItem(
    "device_tracking_pending_start_at",
  );
  const trackingReportedClose = localStorage.getItem(
    "device_tracking_last_reported_close_at",
  );

  // Preserve customer reminder cooldown timestamps and last visited pages
  const customerReminderKeys: { key: string; value: string }[] = [];
  const lastVisitedKeys: { key: string; value: string }[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith("customer_reminder_")) {
      const value = localStorage.getItem(key);
      if (value) {
        customerReminderKeys.push({ key, value });
      }
    }
    if (key && key.startsWith("lastVisitedPage_")) {
      const value = localStorage.getItem(key);
      if (value) {
        lastVisitedKeys.push({ key, value });
      }
    }
  }

  originalClear.apply(localStorage);
  if (lastActiveAccount) {
    localStorage.setItem("last_active_account", lastActiveAccount);
  }
  if (defaultRole) {
    localStorage.setItem("default_role", defaultRole);
  }
  if (deviceId) {
    localStorage.setItem("device_id", deviceId);
  }
  if (trackingPendingClose) {
    localStorage.setItem("device_tracking_pending_close_at", trackingPendingClose);
  }
  if (trackingPendingStart) {
    localStorage.setItem("device_tracking_pending_start_at", trackingPendingStart);
  }
  if (trackingReportedClose) {
    localStorage.setItem("device_tracking_last_reported_close_at", trackingReportedClose);
  }
  customerReminderKeys.forEach(({ key, value }) => {
    localStorage.setItem(key, value);
  });
  lastVisitedKeys.forEach(({ key, value }) => {
    localStorage.setItem(key, value);
  });
};

// NOTE: Browser-extension (chrome/moz/safari) error noise is now filtered
// internally by react-3rddigital-exception-tracking's hardened global handlers,
// so no local extension-error filtering is needed here.

setupExceptionTracking({
  url: process.env.REACT_APP_APPUPDATE_BASE_URL || "",
  apiKey: process.env.REACT_APP_APPUPDATE_API_KEY || "",
  projectKey: process.env.REACT_APP_UPDATE_KEY || "",
  enabled: process.env.REACT_APP_UPDATE_KEY == "1" ? true : false,
  allowedInDevMode: false,
  reactTrackingEnabled: true,
  capacitorTrackingEnabled: Capacitor.isNativePlatform(),
  enrichWithCapacitor: Capacitor.isNativePlatform(),
  installGlobalHandlers: true,
  captureUnhandledRejections: true,
});

if (Capacitor.isNativePlatform()) {
  let isHandlingException = false;
  NativeExceptionHandler.addListener("nativeException", async (event) => {
    if (isHandlingException) return;
    isHandlingException = true;
    try {
      if (event?.uploadedByNative) {
        await NativeExceptionHandler.releaseExceptionHold({ handled: true });
        return;
      }
      await NativeExceptionHandler.releaseExceptionHold({ handled: false });
    } catch (e) {
      console.error("Error in native exception handler:", e);
      await NativeExceptionHandler.releaseExceptionHold({
        handled: false,
      }).catch(() => {});
    } finally {
      isHandlingException = false;
    }
  });

  NativeExceptionHandler.configure({
    url: process.env.REACT_APP_APPUPDATE_BASE_URL || "",
    apiKey: process.env.REACT_APP_APPUPDATE_API_KEY || "",
    projectKey: process.env.REACT_APP_UPDATE_KEY || "",
    enabled: true,
    nativeFallbackEnabled: true,
    executeOriginalHandler: true,
    forceToQuit: false,
    holdTimeoutMs: 5000,
  });
}

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement,
);

root.render(
  //<React.StrictMode>
  //<ExceptionBoundary FallbackComponent={ErrorFallbackPage}>
  <div className="h-100">
    <App />
  </div>,
  //</ExceptionBoundary>,
  //</React.StrictMode>,
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
